// ════════════════════════════════════════════════════════════════════
// src/storeVisits/newVisit.js  —  Launcher (Sprint 2.3 part 5)
//
// Adds a "New Store Visit" button to the admin Store Visits tab. Clicking
// shows a Detailed / Short chooser, then opens the chosen form in a
// full-screen overlay. The form renderers handle their own close (they
// remove the overlay element).
//
// Wiring (one call from the Store Visits tab render in visitTargets.js):
//     renderNewVisitButton(containerEl);
// or, if you just want the global trigger anywhere:
//     <button id="x"></button>  +  document.getElementById('x')
//        .addEventListener('click', () => launchVisitForm('detailed'));
//
// Prefer renderNewVisitButton / addEventListener over inline onclick
// (inline handlers have bitten this codebase — v88.1, clock screen).
// ════════════════════════════════════════════════════════════════════

import { renderDetailedForm } from './form-detailed.js';
import { renderShortForm } from './form-short.js';
import { adminToken } from '../shared/admin-token.js';   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)

function makeOverlay() {
  const overlay = document.createElement('div');
  overlay.className = 'svr-form-overlay';
  overlay.style.cssText =
    'position:fixed;inset:0;background:var(--card);z-index:10000;overflow:auto;padding:24px;';
  document.body.appendChild(overlay);
  return overlay;
}

// Open the form directly (skips the chooser) — type is 'detailed' | 'short'
function launchVisitForm(visitType) {
  const overlay = makeOverlay();
  if (visitType === 'short') renderShortForm(overlay);
  else renderDetailedForm(overlay);
}

function getToken() { return adminToken(); }   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)
function getWorkerUrl() {
  if (typeof DB !== 'undefined' && typeof DB.workerUrl === 'function') return DB.workerUrl();
  return '';
}

// Re-open an EXISTING report in the form to edit it (AM/HO only — the Edit button
// in the read-only detail view is gated, this just opens the editor). `preloaded`
// is the full visit row the caller already has (the detail overlay does), so we
// skip a refetch; otherwise we fetch it. Routes short reports to the short form and
// detailed reports to the detailed form, each in edit mode. v126.1
async function launchEditVisitForm(visitId, preloaded) {
  if (!visitId) return;
  const overlay = makeOverlay();
  let v = preloaded || null;
  if (!v) {
    overlay.innerHTML = '<div style="padding:40px;text-align:center;color:var(--sub);font:15px system-ui;">Loading report…</div>';
    try {
      const res = await fetch(getWorkerUrl() + '/api/store-visits/' + encodeURIComponent(visitId), { cache: 'no-store',
        headers: { 'Authorization': 'Bearer ' + getToken() },
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      v = await res.json();
    } catch (e) {
      overlay.innerHTML =
        '<div style="padding:40px;text-align:center;color:var(--danger-deep);font:15px system-ui;">' +
          'Could not load this report to edit.<br><br>' +
          '<button type="button" id="svr-edit-err-close" style="border:1px solid var(--border-strong);background:var(--card);' +
            'border-radius:8px;padding:8px 16px;cursor:pointer;">Close</button></div>';
      const c = overlay.querySelector('#svr-edit-err-close');
      if (c) c.addEventListener('click', function () { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); });
      return;
    }
  }
  overlay.innerHTML = '';
  if (v && v.visit_type === 'short') renderShortForm(overlay, { visitId: visitId, existingData: v });
  else renderDetailedForm(overlay, { visitId: visitId, existingData: v });
}

// Detailed / Short chooser modal
function showVisitChooser() {
  const modal = document.createElement('div');
  modal.className = 'svr-chooser-modal';
  modal.style.cssText =
    'position:fixed;inset:0;background:var(--overlay);z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px;';
  modal.innerHTML =
    '<div style="background:var(--card);border-radius:14px;padding:24px;max-width:380px;width:100%;font-family:system-ui,-apple-system,sans-serif;box-shadow:0 20px 50px rgba(0,0,0,0.25);">' +
      '<h3 style="margin:0 0 4px;color:var(--text);">New Store Visit</h3>' +
      '<p style="margin:0 0 20px;color:var(--sub);font-size:14px;">Choose the visit type.</p>' +
      '<button type="button" data-choice="detailed" style="display:block;width:100%;text-align:left;padding:14px 16px;margin-bottom:12px;border:1px solid #2563eb;background:var(--info-bg);border-radius:10px;cursor:pointer;">' +
        '<div style="font-weight:700;color:var(--info-deep);">Detailed Visit</div>' +
        '<div style="font-size:13px;color:var(--info-deep);margin-top:2px;">Full report — all 14 areas, photos, Shift checks.</div>' +
      '</button>' +
      '<button type="button" data-choice="short" style="display:block;width:100%;text-align:left;padding:14px 16px;margin-bottom:20px;border:1px solid var(--border-strong);background:var(--surface-2);border-radius:10px;cursor:pointer;">' +
        '<div style="font-weight:700;color:var(--text);">Short Visit</div>' +
        '<div style="font-size:13px;color:var(--sub);margin-top:2px;">Quick check — overall rating, action plan, photos.</div>' +
      '</button>' +
      '<button type="button" data-choice="cancel" style="display:block;width:100%;padding:10px;border:none;background:transparent;color:var(--sub);cursor:pointer;font-size:14px;">Cancel</button>' +
    '</div>';
  document.body.appendChild(modal);

  function close() { if (modal.parentNode) modal.parentNode.removeChild(modal); }

  modal.querySelectorAll('button[data-choice]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      const choice = btn.dataset.choice;
      close();
      if (choice === 'detailed') launchVisitForm('detailed');
      else if (choice === 'short') launchVisitForm('short');
      // 'cancel' just closes
    });
  });

  // Click outside the card to cancel
  modal.addEventListener('click', function (e) {
    if (e.target === modal) close();
  });
}

// Render a "New Store Visit" button into a container
function renderNewVisitButton(container) {
  if (!container) { console.warn('[newVisit] container required'); return; }
  container.innerHTML =
    '<button type="button" class="svr-new-visit-btn" ' +
      'style="padding:10px 18px;border:none;background:#2563eb;color:#fff;border-radius:8px;cursor:pointer;font-weight:600;font-size:14px;">' +
      '+ New Store Visit</button>';
  const btn = container.querySelector('.svr-new-visit-btn');
  if (btn) btn.addEventListener('click', showVisitChooser);
}

// Window re-exposure (per ELC Shift module convention)
window.launchVisitForm = launchVisitForm;
window.launchEditVisitForm = launchEditVisitForm;
window.showVisitChooser = showVisitChooser;
window.renderNewVisitButton = renderNewVisitButton;

export { launchVisitForm, launchEditVisitForm, showVisitChooser, renderNewVisitButton };
